import { FormControl, FormHelperText } from "@mui/material";
import { FC, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { ICustomField, Log, ProductionRecurringFormFields } from "@ryde/shared";

import { GroupTitleTemplate } from "../group_title.template";
import { SwitchTemplate } from "../switch.template";
import { normalizeRecurringProductionTimesFieldData } from "./recurring-production.utils";
import { TimeListItemRow } from "./time-list-item-row";
import { TimeListItemAdd } from "./time-list-item.add";
import { timeItemSerialized } from "./time-list-item.models";

export const RecurringProductionLogic: FC<ICustomField> = ({
  name,
  formAttributes,
}) => {
  const { t } = useTranslation();
  const { control, setValue, watch } = useFormContext();

  const enableRecurringFieldName = `${name}.${ProductionRecurringFormFields.enableRecurring}`;
  const timesFieldName = `${name}.${ProductionRecurringFormFields.times}`;

  const timesListWatched = watch(timesFieldName);

  const timesList = useMemo(() => {
    const timesListValue = normalizeRecurringProductionTimesFieldData(
      timesListWatched,
      timesFieldName,
      setValue,
    );
    Log.debug(
      "recurring production times list normalized value",
      timesListValue,
    );
    return timesListValue;
  }, [timesFieldName, setValue, timesListWatched]);

  const removeHandler = useEvent((index: number) => {
    Log.debug("Remove time item handler fired for index ", index);
    const updatedList = timesList?.filter((_, i) => i !== index) ?? [];
    setValue(timesFieldName, updatedList, {
      shouldDirty: true,
      shouldValidate: true,
    });
  });

  const { disabled } = formAttributes || {};

  return (
    <div className="recurring-production-container">
      <div>
        <GroupTitleTemplate
          name={name}
          labelTranslated={t("produceButton.formLabel.recurring.title")}
        />

        <p className="recurring-production-description">
          {t("produceButton.formLabel.recurring.description")}
        </p>
      </div>

      <div>
        <SwitchTemplate
          name={enableRecurringFieldName}
          labelTranslated={t(
            "produceButton.formLabel.recurring.enableRecurring",
          )}
        />
      </div>

      <Controller
        name={timesFieldName}
        control={control}
        render={({ fieldState: { error } }) => {
          return (
            <FormControl
              error={!!error}
              data-e2e-template="recurring-production-times-list"
            >
              <div className="recurring-production-times-container">
                {timesList?.map((timeItem, index) => {
                  return (
                    <TimeListItemRow
                      key={`${index}_${timeItemSerialized(timeItem)}`}
                      fieldName={timesFieldName}
                      index={index}
                      removeHandler={removeHandler}
                      parentControl={control}
                      parentSetValue={setValue}
                      disabledEdit={disabled}
                      disabledRemove={disabled}
                    />
                  );
                })}

                {!disabled && (
                  <TimeListItemAdd
                    fieldName={timesFieldName}
                    index={timesList.length}
                    setValueOuter={setValue}
                    formAttributes={formAttributes}
                  />
                )}
              </div>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          );
        }}
      />
    </div>
  );
};
